import {useEffect, useRef, useState} from "react";
import {
  SettingParam,
  Action,
  ActionTypes,
  BookingAction,
  TransferCallAction,
} from "@/types/node";
import {Button} from "../ui/Button";
import {
  ArrowLeftRight,
  Calendar,
  ChevronLeft,
  Plus,
  Zap,
  ChevronRight,
  Loader2,
  X,
  RefreshCw,
} from "lucide-react";
import {Icon} from "@/components/ui/icon";
import {Tooltip, TooltipTrigger, TooltipContent} from "@/components/ui/tooltip";
import {
  DropdownDialogItem,
  DropdownDialogItemContent,
  DropdownDialogItemTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/Pagination";

import {attachActionsProxy, getActionsProxy} from "../../api";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {DialogHeader} from "../ui/dialog";
import {DialogTitle} from "@radix-ui/react-dialog";
import {httpModeColors} from "@/constants/apiMethods";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {CustomAction} from "@/api/services/customAction";
import CalComIcon from "@/components/icons/Cal";
import GoHighLevel from "@/components/icons/GoHighLevel";
import {toast} from "@/hooks/useToast";
import {capitalize} from "@/utils/index";
import {cn} from "@/lib/utils";

const ActionTypeMapping = {
  [ActionTypes.REAL_TIME_BOOKING]: "booking",
  [ActionTypes.LIVE_TRANSFER]: "transfer",
  [ActionTypes.CUSTOM_ACTION]: "custom",
};

const ActionsTableHeader = (actionType: string) => {
  switch (actionType) {
    case ActionTypes.REAL_TIME_BOOKING:
      return (
        <>
          <TableHead className="w-16"></TableHead>
          <TableHead className="w-full">Calendar Event Title</TableHead>
          <TableHead className="w-40 whitespace-nowrap">Booking Date</TableHead>
        </>
      );
    case ActionTypes.LIVE_TRANSFER:
      return (
        <>
          <TableHead className="w-40">Number to</TableHead>
          <TableHead className="w-full">
            When should the transfer take place?
          </TableHead>
        </>
      );
    case ActionTypes.CUSTOM_ACTION:
      return (
        <>
          <TableHead className="w-16"></TableHead>
          <TableHead className="w-auto">Action Name</TableHead>
          <TableHead className="w-25">Method</TableHead>
        </>
      );
    default:
      return null;
  }
};

const ActionsTableBody = (actionType: string, action: Action) => {
  switch (actionType) {
    case ActionTypes.REAL_TIME_BOOKING: {
      const bookingAction = action as BookingAction;
      const firstApptDate = new Date(bookingAction.config?.first_appt_date);
      const formattedDate =
        firstApptDate.getDate().toString().padStart(2, "0") +
        " " +
        firstApptDate.toLocaleString("default", {month: "long"}) +
        ", " +
        firstApptDate.getFullYear();
      const bookingType = bookingAction.config?.type;
      return (
        <>
          <TableCell className="font-medium w-16">
            {bookingType === "calcom" ? (
              <CalComIcon
                width={20}
                height={20}
                className="text-primary-accent"
              />
            ) : (
              <GoHighLevel
                width={20}
                height={20}
                className="text-primary-accent"
              />
            )}
          </TableCell>
          <TableCell className="font-medium w-full min-w-0">
            {bookingAction.name || "N/A"}
          </TableCell>
          <TableCell className="w-40">{formattedDate || "N/A"}</TableCell>
        </>
      );
    }
    case ActionTypes.LIVE_TRANSFER: {
      const transferAction = action as TransferCallAction;
      return (
        <>
          <TableCell className="w-40">
            {transferAction.phone_number || "N/A"}
          </TableCell>
          <TableCell className="w-full">
            <span className="line-clamp-2 overflow-hidden text-ellipsis break-words">
              {transferAction.trigger}
            </span>
          </TableCell>
        </>
      );
    }
    case ActionTypes.CUSTOM_ACTION: {
      const customAction = action as CustomAction;
      return (
        <>
          <TableCell className="pr-0 w-16">
            <Icon className="bg-white text-label border">
              {customAction.icon ? (
                <img
                  className="aspect-square h-full w-full"
                  src={customAction.icon}
                />
              ) : (
                <Zap className="w-5 h-5" />
              )}
            </Icon>
          </TableCell>
          <TableCell className="w-auto min-w-0">
            <div className="leading-none min-w-0">
              <h3 className="text-sm text-primary font-medium truncate">
                {customAction.name}
              </h3>
              <span
                className="text-sm text-secondary truncate block"
                title={customAction.url}
              >
                {customAction.url}
              </span>
            </div>
          </TableCell>
          <TableCell className="w-25">
            <div className="flex items-center">
              <div
                className={`${httpModeColors[customAction.http_method?.toLowerCase() || "label"]} text-sm font-medium px-1.5 py-1 rounded-md`}
              >
                {customAction.http_method.toUpperCase() || "N/A"}
              </div>
            </div>
          </TableCell>
        </>
      );
    }
    default:
      return null;
  }
};

export const ActionTable = ({
  handleActionSelect,
  onDialogClose,
  actionType,
  param,
  selectedActions,
}: {
  handleActionSelect: (action: CustomAction) => void;
  onDialogClose?: () => void;
  actionType: string;
  param: SettingParam;
  selectedActions: Action[];
}) => {
  const [actions, setActions] = useState<CustomAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 6;
  const modelId = FlowBuilderSettings.modelId;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchActions(actionType, page);
  };

  const handleActionClick = (action: CustomAction) => {
    handleActionSelect(action);
    onDialogClose && onDialogClose();
  };

  const fetchActions = async (actionType: string, page: number = 1) => {
    try {
      setLoading(true);
      const data = await getActionsProxy({
        node_id: modelId,
        action_type: ActionTypeMapping[actionType],
        page_size: itemsPerPage,
        page_number: page,
      });

      setActions(data.items || []);
      setTotalItems(data.total || 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchActions(actionType, 1);
  }, [actionType]);

  useEffect(() => {
    if (searchTerm.length === 0) {
      fetchActions(actionType, currentPage);
    } else {
      // For search, we'll fetch from page 1 and filter on server side later
      // For now, keeping client-side search but resetting to page 1
      setCurrentPage(1);
      fetchActions(actionType, 1);
    }
  }, [searchTerm]);

  return (
    <div className="my-4 px-4">
      {/* <div className="flex items-center justify-between mb-4 gap-3">
        <Input
          containerClassName="w-full"
          startContent={<SearchIcon className="w-4 h-4 stroke-[#AAB5C5]" />}
          placeholder="Search by name or ID"
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
        />
      </div> */}
      <div className="flex flex-col">
        <Table className="w-full max-w-full table-fixed rounded-lg border text-primary">
          <TableHeader>
            <TableRow>{ActionsTableHeader(actionType)}</TableRow>
          </TableHeader>
          <TableBody className="border-b">
            {loading ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <div className="flex justify-center items-center">
                    <Loader2 className="size-10 animate-spin" />
                  </div>
                </TableCell>
              </TableRow>
            ) : actions.length == 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <div className="flex justify-center items-center">
                    <span className="text-sm text-secondary">
                      No actions found
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              actions.map((action, index) => (
                <TableRow
                  key={action.action_id + index}
                  onClick={() => {
                    handleActionClick(action);
                  }}
                  className={`cursor-pointer hover:bg-muted overflow-hidden text-ellipsis break-words ${
                    selectedActions.some(
                      a => a.action_id === action.action_id,
                    ) || action.run_action_before_call_start
                      ? "opacity-50 pointer-events-none cursor-not-allowed"
                      : ""
                  }`}
                >
                  {ActionsTableBody(actionType, action as unknown as Action)}
                </TableRow>
              ))
            )}
          </TableBody>
          {totalItems > itemsPerPage && (
            <TableFooter className="border-b border-t-0">
              <TableRow>
                <TableCell colSpan={3}>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            if (currentPage > 1)
                              handlePageChange(currentPage - 1);
                          }}
                          isActive={currentPage > 1}
                          className={`${currentPage > 1 ? "cursor-pointer" : "pointer-events-none"}`}
                        >
                          <ChevronLeft className="h-4 w-4 text-secondary" />
                        </PaginationPrevious>
                      </PaginationItem>

                      {(() => {
                        const totalPages = Math.ceil(totalItems / itemsPerPage);
                        const pages = [];
                        const maxVisiblePages = 1;

                        let shouldShowLeftEllipsis = false;
                        let shouldShowRightEllipsis = false;

                        for (let i = 1; i <= totalPages; i++) {
                          if (
                            i === 1 || // Always show first page
                            i === totalPages || // Always show last page
                            (i >= currentPage - maxVisiblePages &&
                              i <= currentPage + maxVisiblePages) // Show pages in range
                          ) {
                            pages.push(
                              <PaginationItem key={i}>
                                <PaginationLink
                                  href="#"
                                  onClick={e => {
                                    e.preventDefault();
                                    handlePageChange(i);
                                  }}
                                  isActive={currentPage === i}
                                >
                                  {i}
                                </PaginationLink>
                              </PaginationItem>,
                            );
                          } else {
                            // left ellipsis
                            if (
                              i < currentPage - maxVisiblePages &&
                              !shouldShowLeftEllipsis
                            ) {
                              pages.push(
                                <PaginationItem key="left-ellipsis">
                                  <span className="px-2">...</span>
                                </PaginationItem>,
                              );
                              shouldShowLeftEllipsis = true;
                            }

                            // right ellipsis
                            if (
                              i > currentPage + maxVisiblePages &&
                              !shouldShowRightEllipsis
                            ) {
                              pages.push(
                                <PaginationItem key="right-ellipsis">
                                  <span className="px-2">...</span>
                                </PaginationItem>,
                              );
                              shouldShowRightEllipsis = true;
                            }
                          }
                        }
                        return pages;
                      })()}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            if (
                              currentPage < Math.ceil(totalItems / itemsPerPage)
                            )
                              handlePageChange(currentPage + 1);
                          }}
                          isActive={
                            currentPage < Math.ceil(totalItems / itemsPerPage)
                          }
                          className={`${
                            currentPage < Math.ceil(totalItems / itemsPerPage)
                              ? "cursor-pointer"
                              : "pointer-events-none"
                          }`}
                        >
                          <ChevronRight className="h-4 w-4 text-secondary" />
                        </PaginationNext>
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
};

export const SettingAction = ({
  param,
  handleChange,
  disabled = false,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: Action[]) => void;
  disabled: boolean;
}) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const selectedActions = (param.value || []) as Action[];
  const modelId = FlowBuilderSettings.modelId;
  const closeDialog = () => setIsDialogOpen(false);
  const [isReattaching, setIsReattaching] = useState(false);
  return (
    <div className="mb-4 flex flex-nowrap gap-2 flex-col">
      <DropdownMenu
        modal={false}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      >
        <label className="text-sm font-medium text-label mb-2 flex items-center justify-between">
          {param.label}
        </label>
        {selectedActions.length > 0 && (
          <div
            className={`flex items-center gap-2 flex-wrap w-full ${
              disabled
                ? "opacity-50 pointer-events-none cursor-not-allowed"
                : ""
            }`}
          >
            {selectedActions.map(action => {
              let actionDisplayName = action.name || "N/A";
              let actionIcon = <Zap className="w-4.5 h-4.5" />;

              switch (action.type) {
                case ActionTypes.REAL_TIME_BOOKING:
                  actionDisplayName =
                    (action as BookingAction).config?.title ||
                    action.name ||
                    "N/A";
                  actionIcon = <Calendar className="w-4.5 h-4.5" />;
                  break;
                case ActionTypes.LIVE_TRANSFER:
                  actionDisplayName =
                    (action as TransferCallAction).phone_number || "N/A";
                  actionIcon = <ArrowLeftRight className="w-4.5 h-4.5" />;
                  break;
                case ActionTypes.CUSTOM_ACTION:
                  actionIcon = <Zap className="w-4.5 h-4.5" />;
                  break;
              }
              return (
                <div
                  key={action.action_id}
                  className={cn(
                    "text-xs text-primary font-medium p-2 border rounded-md flex items-center gap-2 max-w-full",
                    action.missing && "border-red-500",
                  )}
                >
                  <Icon
                    className={cn(
                      "text-primary-accent border-1 bg-primary-accent w-4.5 h-4.5",
                      action.missing && "text-red-500",
                    )}
                  >
                    {actionIcon}
                  </Icon>

                  <span
                    className={cn("truncate max-w-full", {
                      "text-red-500": action.missing,
                    })}
                  >
                    {actionDisplayName}
                  </span>

                  {action.missing && (
                    <Tooltip delayDuration={0} disableHoverableContent={true}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          className="ml-auto"
                          disabled={isReattaching}
                          onClick={async () => {
                            setIsReattaching(true);
                            try {
                              await attachActionsProxy({
                                node_id: modelId,
                                actions: [action.action_id],
                              });
                              const updated = selectedActions.map(a => {
                                if (a.action_id === action.action_id) {
                                  const {missing, ...cleaned} = a;
                                  return cleaned;
                                }
                                return a;
                              });
                              handleChange(param, updated);
                              toast({
                                title: "Action attached",
                                variant: "default",
                              });
                              setIsReattaching(false);
                            } catch (error) {
                              console.error(error);
                              toast({
                                title: "Error attaching action",
                                variant: "destructive",
                              });
                              setIsReattaching(false);
                            }
                          }}
                        >
                          <div className={isReattaching && "animate-spin"}>
                            <RefreshCw className="text-slate-400" />
                          </div>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" sideOffset={10}>
                        Reattach Action
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <Tooltip delayDuration={0} disableHoverableContent={true}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        className="ml-auto"
                        onClick={() => {
                          const newSelectedActions = selectedActions.filter(
                            a => a.action_id !== action.action_id,
                          );
                          handleChange(param, newSelectedActions);
                        }}
                      >
                        <X className="text-slate-400" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" sideOffset={10}>
                      Remove Action
                    </TooltipContent>
                  </Tooltip>
                </div>
              );
            })}
            <DropdownMenuTrigger ref={triggerRef} className="flex-grow flex">
              <Button variant="outline" className="px-3 h-9.5 w-9.5" asDiv>
                <Plus className="text-label w-4 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
          </div>
        )}
        {selectedActions.length === 0 && (
          <DropdownMenuTrigger asChild ref={triggerRef} className="w-full">
            <Button
              variant="outline"
              className={`w-full px-3`}
              disabled={disabled}
              asDiv
            >
              <Zap className="text-violet w-4 h-4" />
              Add Action
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent
          triggerRef={triggerRef}
          className={`w-[400px] min-w-[400px] max-w-[400px]`}
          side="bottom"
          align="end"
          sideOffset={8}
        >
          <DropdownDialogItem>
            <DropdownDialogItemTrigger>
              <div className="flex items-start text-left gap-3">
                <Icon className="bg-muted text-label border-0">
                  <Calendar className="w-4.5 h-4.5" />
                </Icon>
                <div className="leading-none">
                  <h3 className="text-sm text-primary font-medium">
                    Real-Time Booking
                  </h3>
                  <span className="text-sm text-secondary">
                    Schedule live appointments during the call.
                  </span>
                </div>
              </div>
            </DropdownDialogItemTrigger>
            <DropdownDialogItemContent>
              <DialogHeader>
                <DialogTitle className="text-primary font-medium text-lg">
                  Add Real-Time Booking
                </DialogTitle>
              </DialogHeader>
              <ActionTable
                param={param}
                handleActionSelect={action => {
                  handleChange(param, [
                    ...selectedActions,
                    action as unknown as Action,
                  ]);
                }}
                onDialogClose={closeDialog}
                selectedActions={selectedActions}
                actionType={ActionTypes.REAL_TIME_BOOKING}
                // selectedNodeId={selectedNodeId}
              />
            </DropdownDialogItemContent>
          </DropdownDialogItem>
          <DropdownDialogItem>
            <DropdownDialogItemTrigger>
              <div className="flex items-start text-left gap-3">
                <Icon className="bg-muted text-label border-0">
                  <ArrowLeftRight className="w-4.5 h-4.5" />
                </Icon>
                <div className="leading-none">
                  <h3 className="text-sm text-primary font-medium">
                    Call Transfer
                  </h3>
                  <span className="text-sm text-secondary">
                    Transfer the call live to a human.
                  </span>
                </div>
              </div>
            </DropdownDialogItemTrigger>
            <DropdownDialogItemContent>
              <DialogHeader>
                <DialogTitle className="text-primary font-medium text-lg">
                  Add Call Transfer
                </DialogTitle>
              </DialogHeader>
              <ActionTable
                param={param}
                handleActionSelect={action =>
                  handleChange(param, [
                    ...selectedActions,
                    action as unknown as Action,
                  ])
                }
                onDialogClose={closeDialog}
                selectedActions={selectedActions}
                actionType={ActionTypes.LIVE_TRANSFER}
                // selectedNodeId={selectedNodeId}
              />
            </DropdownDialogItemContent>
          </DropdownDialogItem>
          <DropdownDialogItem>
            <DropdownDialogItemTrigger>
              <div className="flex items-start gap-3">
                <Icon className="bg-muted text-label border-0">
                  <Zap className="w-4.5 h-4.5" />
                </Icon>
                <div className="leading-none">
                  <h3 className="text-sm text-primary font-medium">
                    Custom Actions
                  </h3>
                  <span className="text-sm text-secondary">
                    Allow your assistants to automatically trigger custom API.
                  </span>
                </div>
              </div>
            </DropdownDialogItemTrigger>
            <DropdownDialogItemContent>
              <DialogHeader>
                <DialogTitle className="text-primary font-medium text-lg">
                  Add Custom Actions
                </DialogTitle>
              </DialogHeader>
              <ActionTable
                param={param}
                handleActionSelect={action =>
                  handleChange(param, [
                    ...selectedActions,
                    action as unknown as Action,
                  ])
                }
                onDialogClose={closeDialog}
                selectedActions={selectedActions}
                actionType={ActionTypes.CUSTOM_ACTION}
                // selectedNodeId={selectedNodeId}
              />
            </DropdownDialogItemContent>
          </DropdownDialogItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
